import React, { PureComponent } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Settings } from 'luxon';
import { requestAsync, querySelectors } from 'redux-query';
import { connect } from 'react-redux';
import { Spinner, HeaderText, Text } from '@el8/vital';
import { Box } from 'components';
import { updateBookingInfo } from 'modules/booking';
import { getPracticeQuery, getPractice } from 'modules/practice';
import {
  State,
  RQPracticeResponse,
  ResponseStatus,
  PracticeInfo,
  Dispatch,
  RQServiceLocationsResponse,
} from 'types';
import styles from './Booking.less';
import { getServiceLocationsQuery } from '../../../modules/serviceLocations';

type OwnProps = {
  children?: React.ReactNode;
  practiceId?: number;
};

type StateProps = {
  isLoadingFinished: boolean;
  requestStatus: number;
  practice: PracticeInfo;
};

type DispatchProps = {
  fetchLocations: () => Promise<RQServiceLocationsResponse>;
  fetchPractice: () => Promise<RQPracticeResponse>;
  selectPhysician: (physicianId: number) => void;
};

const mapStateToProps = (state: State, { practiceId }: OwnProps): StateProps => ({
  isLoadingFinished: querySelectors.isFinished(state.queries, getPracticeQuery(practiceId)),
  practice: getPractice(state, { practiceId }),
  requestStatus: querySelectors.status(state.queries, getPracticeQuery(practiceId)),
});

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps): DispatchProps => ({
  fetchLocations: (): Promise<RQServiceLocationsResponse> =>
    dispatch(requestAsync(getServiceLocationsQuery(ownProps.practiceId))),
  fetchPractice: (): Promise<RQPracticeResponse> =>
    dispatch(requestAsync(getPracticeQuery(ownProps.practiceId))),
  selectPhysician: (physicianId: number): void => {
    dispatch(updateBookingInfo({ selectedPhysicianId: physicianId }));
  },
});

type BookingProps = OwnProps & StateProps & DispatchProps & RouteComponentProps<OwnProps>;

class Booking extends PureComponent<BookingProps> {
  componentDidMount(): void {
    const { fetchLocations, fetchPractice, location, selectPhysician } = this.props;
    fetchLocations();
    fetchPractice().then(({ body }) => {
      if (body.status === ResponseStatus.success) {
        Settings.defaultZoneName = body.data.timezone;
        // International timezones not supported yet. All timezones will be relative to US timezones
        Settings.defaultLocale = 'en-US';
        const initialPhysicianId = Number(new URLSearchParams(location.search).get('physician_id'));
        if (
          initialPhysicianId &&
          body.data.physicians.find((physician) => physician.id === initialPhysicianId)
        ) {
          selectPhysician(initialPhysicianId);
        }
      }
    });
  }

  render(): JSX.Element {
    const { children, isLoadingFinished, requestStatus } = this.props;

    return (
      <div className={styles.booking}>
        {isLoadingFinished && requestStatus === 200 && (
          <div className={styles.content}>{children}</div>
        )}
        {isLoadingFinished && requestStatus === 404 && (
          <Box className={styles.loaderContainer} align="center" justify="center">
            <HeaderText align="center" legacyVariant="legacyTitleText" sizing="heading-lg" tag="h1">
              Practice does not exist
            </HeaderText>
            <Text align="center">
              {`We weren't able to find the practice that you're looking for. Perhaps it doesn't exist,
              or you're using the wrong URL.`}
            </Text>
            <Text align="center">
              {`If you're sure this is a real practice, please double check the URL and try
              again.`}
            </Text>
          </Box>
        )}
        {!isLoadingFinished && (
          <Box className={styles.loaderContainer} align="center" justify="center">
            <Spinner />
          </Box>
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Booking);
